/**
 * Rocket radar (2026-09-08 owner ask): the current Team GO Rocket lineups
 * (Giovanni, the three leaders, every grunt) read as "which catchable shadow
 * is still missing from my Shiny Dex".
 *
 * The feed (GET /api/events/rocket, LeekDuck via ScrapedDuck) flags per slot
 * Pokémon whether it is the ENCOUNTER (the shadow you get to catch after the
 * battle) and whether that encounter can be shiny. The radar only ever
 * cross-references the Shiny Dex (owner direction): a shadow you already
 * own shiny is a tick, one whose shiny is out and missing is a gap tile, and
 * one GO has never released shiny is drawn plain so the pool still reads
 * complete for the leaders.
 *
 * Pure: the predicates are injected so the shape is testable without the
 * dex store, and the components pass the real ones.
 */
import { raidBossFormId } from "./raidRoster";

export type RocketLineupKind = "boss" | "leader" | "grunt";

export interface RocketSlotPokemonDto {
  name: string;
  speciesId?: number;
  formSuffix?: string;
  types?: string[];
  isEncounter: boolean;
  canBeShiny: boolean;
  image?: string;
}

export interface RocketLineupDto {
  key: string;
  name: string;
  title: string;
  kind: RocketLineupKind;
  type?: string;
  slots: RocketSlotPokemonDto[][];
}

export type RocketShinyStatus = "gap" | "owned" | "no-shiny";

/** One catchable shadow of a lineup, resolved against the Shiny Dex. */
export interface RocketEncounter {
  speciesId: number;
  /** The app's form id for a regional encounter (Alolan Geodude), else undefined. */
  formId?: string;
  name: string;
  canBeShiny: boolean;
  status: RocketShinyStatus;
}

export interface RocketLineupView {
  lineup: RocketLineupDto;
  encounters: RocketEncounter[];
}

export interface RocketDexPredicates {
  /** Does the trainer own this shiny (species, or the form when given)? */
  isPresent: (speciesId: number, formId?: string) => boolean;
  /** Has GO released this shiny (species, or the form when given)? */
  isAvailable: (speciesId: number, formId?: string) => boolean;
}

const LEADER_ORDER: readonly string[] = ["giovanni", "cliff", "arlo", "sierra"];

/** Giovanni first, then Cliff, Arlo, Sierra: the order the game lists them. */
export function rocketLeaderLineups(lineups: readonly RocketLineupDto[]): RocketLineupDto[] {
  return lineups
    .filter((l) => l.kind === "boss" || l.kind === "leader")
    .sort((a, b) => {
      const ia = LEADER_ORDER.indexOf(a.key);
      const ib = LEADER_ORDER.indexOf(b.key);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
}

export function rocketGruntLineups(lineups: readonly RocketLineupDto[]): RocketLineupDto[] {
  return lineups.filter((l) => l.kind === "grunt");
}

/**
 * The catchable shadows of one lineup in slot order, each species once,
 * resolved against the Shiny Dex. A species the feed could not identify is
 * dropped: there is nothing to draw or to tick.
 */
export function rocketLineupEncounters(
  lineup: RocketLineupDto,
  dex: RocketDexPredicates,
): RocketEncounter[] {
  const out: RocketEncounter[] = [];
  const seen = new Set<string>();
  for (const slot of lineup.slots) {
    for (const p of slot) {
      if (!p.isEncounter || typeof p.speciesId !== "number" || p.speciesId < 1) continue;
      const formId = raidBossFormId({ speciesId: p.speciesId, formSuffix: p.formSuffix }) ?? undefined;
      const key = `${p.speciesId}:${formId ?? ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      let status: RocketShinyStatus;
      if (dex.isPresent(p.speciesId, formId)) status = "owned";
      else if (p.canBeShiny && dex.isAvailable(p.speciesId, formId)) status = "gap";
      else status = "no-shiny";
      out.push({
        speciesId: p.speciesId,
        ...(formId ? { formId } : {}),
        name: p.name,
        canBeShiny: p.canBeShiny,
        status,
      });
    }
  }
  return out;
}

/** Every lineup with its encounters, feed order kept. */
export function rocketLineupViews(
  lineups: readonly RocketLineupDto[],
  dex: RocketDexPredicates,
): RocketLineupView[] {
  return lineups.map((lineup) => ({ lineup, encounters: rocketLineupEncounters(lineup, dex) }));
}

/**
 * The grunt pool's shiny gaps as one flat list, each species once across
 * every grunt (Gligar walks with three grunts; the Shiny Dex has one row).
 */
export function rocketGruntShinyGaps(
  lineups: readonly RocketLineupDto[],
  dex: RocketDexPredicates,
): RocketEncounter[] {
  const out: RocketEncounter[] = [];
  const seen = new Set<string>();
  for (const view of rocketLineupViews(rocketGruntLineups(lineups), dex)) {
    for (const e of view.encounters) {
      if (e.status !== "gap") continue;
      const key = `${e.speciesId}:${e.formId ?? ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(e);
    }
  }
  return out;
}

/**
 * The label a grunt row carries beside its type disc: the gender word and,
 * for the typeless ones, the whole name ("Male Grunt", "Decoy Female Grunt").
 */
export function rocketGruntLabel(lineup: RocketLineupDto): string {
  if (!lineup.type) return lineup.name;
  const m = lineup.name.match(/\b(Male|Female)\b/i);
  return m ? m[1]![0]!.toUpperCase() + m[1]!.slice(1).toLowerCase() : lineup.name;
}
